import React, { useState } from "react";
import Modal from "../../../Shared/Components/Modal/Modal";
import { createLearnerAsync } from "../../../Services/AccountService";
import {
  previewUsersCsvAsync,
  uploadUsersCsvAsync,
} from "../../../Services/ClientService";
import { showToast } from "../../../Infrastructure/Notifications/ToastrConfig";

const GroupUserManagement = ({ groupId, clientId, users = [], onUpdate }) => {
  const [addForm, setAddForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
  });
  const [isAdding, setIsAdding] = useState(false);

  const [goodRows, setGoodRows] = useState([]);
  const [badRows, setBadRows] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleAddUser = async () => {
    if (!addForm.firstName || !addForm.lastName || !addForm.email) {
      showToast(false, "All fields are required");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(addForm.email)) {
      showToast(false, "Please enter a valid email address");
      return;
    }

    try {
      setIsAdding(true);
      const response = await createLearnerAsync({
        firstName: addForm.firstName,
        lastName: addForm.lastName,
        email: addForm.email,
        clientId: clientId || undefined,
        groupId: groupId,
      });

      if (response.success) {
        showToast(true, "User added to group successfully");
        setAddForm({ firstName: "", lastName: "", email: "" });
        if (onUpdate) onUpdate();
      } else {
        showToast(false, response.errors?.[0]?.error || "Failed to add user");
      }
    } catch (error) {
      console.error("Error adding user:", error);
      showToast(false, "Failed to add user");
    } finally {
      setIsAdding(false);
    }
  };

  const handleFileChange = async (e) => {
    const selected = e.target.files[0];
    if (!selected) return;

    if (
      selected.type !== "text/csv" &&
      !selected.name.toLowerCase().endsWith(".csv")
    ) {
      showToast(false, "Please upload a CSV file");
      e.target.value = null;
      return;
    }

    setIsProcessing(true);
    setGoodRows([]);
    setBadRows([]);

    const form = new FormData();
    form.append("csvFile", selected);

    try {
      const result = await previewUsersCsvAsync(form);
      const good = result.goodRows || [];
      const bad = result.badRows || [];
      setGoodRows(good);
      setBadRows(bad);

      if (good.length === 0) {
        showToast(false, "No valid users found in CSV");
      } else {
        showToast(
          true,
          `Loaded ${good.length} valid user${good.length !== 1 ? "s" : ""}${bad.length ? ` (${bad.length} invalid rows skipped)` : ""}`,
        );
      }
    } catch (error) {
      console.error("Error previewing CSV:", error);
      showToast(false, "Failed to read CSV. Check headers and file format.");
    } finally {
      setIsProcessing(false);
      e.target.value = null;
    }
  };

  const handleBulkUpload = async () => {
    if (goodRows.length === 0) {
      showToast(false, "Please upload a valid CSV file first");
      return;
    }

    try {
      setIsProcessing(true);
      const response = await uploadUsersCsvAsync({
        clientId: clientId || "",
        users: goodRows,
        groupId: groupId,
      });

      if (response.success) {
        showToast(true, `Successfully added ${goodRows.length} users to group`);
        setGoodRows([]);
        setBadRows([]);
        if (onUpdate) onUpdate();
      } else {
        showToast(false, response.errors?.[0]?.error || "Upload failed");
      }
    } catch (error) {
      console.error("Error bulk uploading:", error);
      showToast(false, "Upload failed");
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadTemplate = () => {
    const csv = "email,firstName,lastName\nuser@example.com,John,Doe\n";
    const blob = new Blob([csv], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const addUserModalBody = (
    <div style={{ padding: "10px 0" }}>
      {[
        { field: "firstName", label: "First Name", type: "text" },
        { field: "lastName", label: "Last Name", type: "text" },
        { field: "email", label: "Email", type: "email" },
      ].map(({ field, label, type }) => (
        <div key={field} style={{ marginBottom: "16px" }}>
          <label
            style={{
              display: "block",
              fontWeight: "600",
              marginBottom: "8px",
              color: "#1e293b",
              fontSize: "0.95rem",
            }}
          >
            {label} <span style={{ color: "#ef4444" }}>*</span>
          </label>
          <input
            type={type}
            value={addForm[field]}
            onChange={(e) =>
              setAddForm({ ...addForm, [field]: e.target.value })
            }
            className="form-control"
            placeholder={`Enter ${label.toLowerCase()}`}
            style={{
              height: "44px",
              fontSize: "1rem",
              borderRadius: "8px",
              border: "2px solid #e2e8f0",
              padding: "0 14px",
            }}
          />
        </div>
      ))}
    </div>
  );

  const bulkUploadModalBody = (
    <div style={{ padding: "10px 0" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          padding: "16px 20px",
          borderRadius: "10px",
          marginBottom: "20px",
          color: "white",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: "600", fontSize: "1rem" }}>
          📋 CSV Format
        </p>
        <ul
          style={{
            margin: "0 0 12px 0",
            paddingLeft: "20px",
            lineHeight: "1.7",
            fontSize: "0.9rem",
          }}
        >
          <li>Columns: email, firstName, lastName</li>
          <li>First row must be the header row</li>
          <li>Invalid or duplicate rows will be skipped</li>
        </ul>
        <button
          onClick={downloadTemplate}
          style={{
            background: "rgba(255,255,255,0.2)",
            color: "white",
            border: "2px solid white",
            borderRadius: "6px",
            padding: "6px 14px",
            cursor: "pointer",
            fontSize: "0.85rem",
            fontWeight: "600",
          }}
        >
          📥 Download Template
        </button>
      </div>

      <div style={{ marginBottom: "16px" }}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "12px 16px",
            border: "2px dashed #667eea",
            borderRadius: "8px",
            cursor: isProcessing ? "not-allowed" : "pointer",
            color: "#667eea",
            fontWeight: "600",
            fontSize: "0.95rem",
          }}
        >
          📁 Choose CSV File
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            disabled={isProcessing}
            style={{ display: "none" }}
          />
        </label>
      </div>

      {goodRows.length > 0 && (
        <div
          style={{
            background: "#f0fdf4",
            border: "1px solid #bbf7d0",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "10px",
          }}
        >
          <p
            style={{
              margin: "0 0 8px 0",
              fontWeight: "600",
              color: "#16a34a",
              fontSize: "0.9rem",
            }}
          >
            ✅ {goodRows.length} user{goodRows.length !== 1 ? "s" : ""} ready to
            upload
          </p>
          {goodRows.slice(0, 3).map((row, i) => (
            <div key={i} style={{ fontSize: "0.85rem", color: "#374151" }}>
              {row.firstName} {row.lastName} — {row.email}
            </div>
          ))}
          {goodRows.length > 3 && (
            <div style={{ fontSize: "0.85rem", color: "#6b7280" }}>
              ...and {goodRows.length - 3} more
            </div>
          )}
        </div>
      )}

      {badRows.length > 0 && (
        <div
          style={{
            background: "#fef2f2",
            border: "1px solid #fecaca",
            borderRadius: "8px",
            padding: "10px 16px",
          }}
        >
          <p
            style={{
              margin: 0,
              fontWeight: "600",
              color: "#dc2626",
              fontSize: "0.9rem",
            }}
          >
            ⚠️ {badRows.length} invalid row{badRows.length !== 1 ? "s" : ""}{" "}
            will be skipped
          </p>
        </div>
      )}
    </div>
  );

  return (
    <>
      <Modal
        modalId="groupAddUserModal"
        title="Add User to Group"
        bodyText={addUserModalBody}
        buttonText={isAdding ? "Adding..." : "Add User"}
        buttonFunc={handleAddUser}
        closeOnConfirm={false}
        disableConfirm={isAdding}
      />
      <Modal
        modalId="groupBulkUploadModal"
        title="Bulk Upload Users"
        bodyText={bulkUploadModalBody}
        buttonText={
          isProcessing
            ? "Uploading..."
            : goodRows.length > 0
              ? `Upload ${goodRows.length} User${goodRows.length !== 1 ? "s" : ""}`
              : "Upload"
        }
        buttonFunc={handleBulkUpload}
        closeOnConfirm={false}
        disableConfirm={isProcessing || goodRows.length === 0}
      />

      {/* Section header with add buttons */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h3
          style={{
            fontSize: "1.5rem",
            fontWeight: "700",
            color: "#1e293b",
            borderBottom: "3px solid #11998e",
            paddingBottom: "12px",
            margin: 0,
          }}
        >
          👥 Group Members
        </h3>
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            data-toggle="modal"
            data-target="#groupAddUserModal"
            style={{
              background: "linear-gradient(135deg, #10b981 0%, #059669 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              fontSize: "0.9rem",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(16, 185, 129, 0.25)",
            }}
          >
            + Add User
          </button>
          <button
            data-toggle="modal"
            data-target="#groupBulkUploadModal"
            style={{
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              border: "none",
              borderRadius: "8px",
              padding: "10px 20px",
              fontSize: "0.9rem",
              fontWeight: "600",
              cursor: "pointer",
              boxShadow: "0 4px 12px rgba(102, 126, 234, 0.25)",
            }}
          >
            📁 Bulk Upload
          </button>
        </div>
      </div>

      {/* User list */}
      {users.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
          {users.map((user) => (
            <div
              key={user.id}
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                background: "#f8fafc",
                borderRadius: "8px",
                padding: "16px 20px",
                border: "1px solid #e2e8f0",
              }}
            >
              <div>
                <div
                  style={{
                    fontSize: "1rem",
                    fontWeight: "600",
                    color: "#1e293b",
                  }}
                >
                  {user.firstName} {user.lastName}
                </div>
                <div style={{ fontSize: "0.85rem", color: "#64748b" }}>
                  {user.email}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "40px",
            color: "#64748b",
            fontSize: "0.95rem",
          }}
        >
          No users in this group yet
        </div>
      )}
    </>
  );
};

export default GroupUserManagement;
